import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext(null);

const IDLE_TIMEOUT = 30 * 60 * 1000;   // 30 minutes
const WARNING_BEFORE = 2 * 60 * 1000;  // show warning 2 min before

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionWarning, setSessionWarning] = useState(false);
  const timerRef = useRef(null);
  const warningRef = useRef(null);

  // ── Reset inactivity timer ───────────────────────────────────────────────
  const resetTimer = useCallback(() => {
    if (!user) return;
    setSessionWarning(false);
    clearTimeout(timerRef.current);
    clearTimeout(warningRef.current);

    warningRef.current = setTimeout(() => setSessionWarning(true), IDLE_TIMEOUT - WARNING_BEFORE);
    timerRef.current = setTimeout(() => {
      localStorage.removeItem('portal_token');
      setUser(null);
      setSessionWarning(false);
    }, IDLE_TIMEOUT);
  }, [user]);

  // ── Listen for activity when logged in ───────────────────────────────────
  useEffect(() => {
    if (!user) return;
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();
    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimer));
      clearTimeout(timerRef.current);
      clearTimeout(warningRef.current);
    };
  }, [user, resetTimer]);

  // ── Bootstrap from stored token ──────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem('portal_token');
    if (token) {
      axios.get(`${BACKEND_URL}/api/portal/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setUser({ ...res.data, token }))
        .catch(() => localStorage.removeItem('portal_token'))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (username, password) => {
    const res = await axios.post(`${BACKEND_URL}/api/portal/login`, { username, password });
    const { token, role } = res.data;
    localStorage.setItem('portal_token', token);
    setUser({ username: res.data.username, role, token });
    return res.data;
  };

  const logout = useCallback(() => {
    clearTimeout(timerRef.current);
    clearTimeout(warningRef.current);
    localStorage.removeItem('portal_token');
    setUser(null);
    setSessionWarning(false);
  }, []);

  const authHeaders = () => ({ Authorization: `Bearer ${user?.token || localStorage.getItem('portal_token')}` });

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, authHeaders, sessionWarning, resetTimer }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
